import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, ArrowRight, Star, ShieldCheck, Globe2, Search, Users, Utensils } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { getRef } from '../lib/referral';
import { HumanCheck } from '../components/HumanCheck';
import ScanPromo from '../components/ScanPromo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', role: '', city: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [humanToken, setHumanToken] = useState('');
  const set = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setErr(''); };

  const c = isEs ? {
    title: 'Agenda tu demo gratuita | Kitxens',
    h1: 'Consigue una demo gratuita y personalizada',
    sub: 'Agenda una consulta con un experto de Kitxens y recibe un diagnóstico sin compromiso — con precios fundadores desde $99/mes.',
    name: 'Nombre y apellidos*', email: 'Email*', phone: 'Teléfono*', business: 'Nombre de tu restaurante / negocio*',
    role: '¿Qué te describe mejor?*', roles: ['Dueño/a de restaurante', 'Gerente / Operaciones', 'Grupo multi-local', 'Agencia / Consultor', 'Otro'],
    city: 'Ciudad (opcional)',
    cta: 'Quiero mi demo', sending: 'Enviando…',
    legal: 'Al solicitar la demo aceptas que te contactemos por email, teléfono o WhatsApp. Tratamos tus datos según nuestra política de privacidad.',
    haveAccount: '¿Ya eres cliente?', login: 'Inicia sesión',
    doneT: '¡Listo! Tu demo está en marcha 🎉', doneD: 'Nuestro equipo te contactará hoy mismo para agendar tu demo personalizada. Revisa tu email y tu WhatsApp.',
    doneCta: 'Mientras tanto, escanea tu web gratis',
    proofT: 'Restaurantes en 3 continentes confían en Kitxens para operar y crecer',
    proofSub: 'Damos a los operadores un departamento tecnológico completo — sin que toquen la tecnología.',
    errFields: 'Completa nombre, email, teléfono, negocio y tu perfil.',
    errHuman: 'Confirma que no eres un robot.',
    errGeneric: 'Algo salió mal. Inténtalo de nuevo.',
    points: ['Diagnóstico Index AI™ de tu presencia online, en vivo', 'Plan personalizado según tu tipo de restaurante', 'Precio fundador bloqueado si decides empezar', 'Sin permanencia — cancela cuando quieras'],
    badges: [['30+', 'métricas analizadas por diagnóstico'], ['24/7', 'IA trabajando para tu restaurante'], ['$99', 'desde /mes, precio fundador'], ['0', 'permanencia — cancela cuando quieras']],
  } : {
    title: 'Get your free demo | Kitxens',
    h1: 'Get a free, personalized demo',
    sub: 'Schedule a consultation with a Kitxens expert and receive a no-commitment diagnostic — with founding pricing starting at $99/mo.',
    name: 'First and last name*', email: 'Email*', phone: 'Phone*', business: 'Restaurant / business name*',
    role: 'Which best describes you?*', roles: ['Restaurant owner', 'Manager / Operations', 'Multi-location group', 'Agency / Consultant', 'Other'],
    city: 'City (optional)',
    cta: 'Get my demo', sending: 'Sending…',
    legal: 'By requesting a demo you agree to be contacted by email, phone or WhatsApp. We handle your info according to our privacy policy.',
    haveAccount: 'Already a client?', login: 'Log in',
    doneT: 'Done! Your demo is on its way 🎉', doneD: 'Our team will reach out today to schedule your personalized demo. Keep an eye on your email and WhatsApp.',
    doneCta: 'Meanwhile, scan your website free',
    proofT: 'Restaurants across 3 continents trust Kitxens to run and grow',
    proofSub: 'We give operators a full technology department — without them ever touching the technology.',
    errFields: 'Fill in name, email, phone, business and your profile.',
    errHuman: "Please confirm you're not a robot.",
    errGeneric: 'Something went wrong. Please try again.',
    points: ['Live Index AI™ diagnostic of your online presence', 'A personalized plan for your restaurant type', 'Founding price locked in if you decide to start', 'No lock-in — cancel anytime'],
    badges: [['30+', 'metrics analyzed per diagnostic'], ['24/7', 'AI working for your restaurant'], ['$99', 'from /mo, founding price'], ['0', 'lock-in — cancel anytime']],
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.business.trim() || !form.role) {
      setErr(c.errFields); return;
    }
    if (!humanToken) { setErr(c.errHuman); return; }
    setBusy(true);
    try {
      const [first, ...rest] = form.name.trim().split(' ');
      const r = await fetch(`${API}/api/lead`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': humanToken },
        body: JSON.stringify({
          email: form.email.trim(), first_name: first, last_name: rest.join(' '),
          phone: form.phone.trim(), company: form.business.trim(), city: form.city.trim(),
          source: 'demo', interest: 'Free personalized demo', locale: lang,
          notes: `Demo request — profile: ${form.role}`,
          source_page: '/demo',
          metadata: { role: form.role, ...(getRef() ? { ref: getRef() } : {}) },
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d?.detail === 'string' ? d.detail : c.errGeneric); setBusy(false); return; }
      setDone(true);
    } catch { setErr(c.errGeneric); setBusy(false); }
  };

  const inputCls = 'w-full h-[52px] sm:h-12 rounded-xl border border-neutral-300 bg-white px-4 text-base sm:text-[14.5px] text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-[#F77F11] transition-colors';

  return (
    <div className="min-h-screen bg-[#FAFAF8] pt-36 md:pt-40 pb-20" data-testid="demo-page">
      <SEO path="/demo" title={c.title} description={c.sub} />
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

        {/* ---- Left: headline + form ---- */}
        <div>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900 leading-[1.05]">
            {c.h1}
          </motion.h1>
          <p className="mt-4 text-[15.5px] text-neutral-600 leading-relaxed max-w-lg">{c.sub}</p>

          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-8 rounded-2xl border-2 border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="demo-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <h2 className="mt-4 text-xl font-bold text-neutral-900">{c.doneT}</h2>
              <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed">{c.doneD}</p>
              <Link to="/index-ai" className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-bold text-[#F77F11] hover:underline" data-testid="demo-success-scan-link">
                <Search className="w-4 h-4" /> {c.doneCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ) : (
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-8 rounded-2xl border-2 border-[#F77F11] bg-white p-6 sm:p-8 shadow-[0_10px_40px_-18px_rgba(247,127,17,0.45)]" data-testid="demo-form">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <input value={form.name} onChange={set('name')} placeholder={c.name} className={inputCls} data-testid="demo-name" />
                <select value={form.role} onChange={set('role')} className={`${inputCls} ${form.role ? '' : 'text-neutral-400'}`} data-testid="demo-role">
                  <option value="">{c.role}</option>
                  {c.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input type="email" value={form.email} onChange={set('email')} placeholder={c.email} className={inputCls} data-testid="demo-email" />
                <input value={form.phone} onChange={set('phone')} placeholder={c.phone} className={inputCls} data-testid="demo-phone" />
                <input value={form.business} onChange={set('business')} placeholder={c.business} className={`${inputCls} sm:col-span-2`} data-testid="demo-business" />
                <input value={form.city} onChange={set('city')} placeholder={c.city} className={`${inputCls} sm:col-span-2`} data-testid="demo-city" />
              </div>
              {err && <p className="mt-3 text-[13px] text-red-500" data-testid="demo-error">{err}</p>}
              <HumanCheck className="mt-4" onToken={setHumanToken} />
              <button type="submit" disabled={busy}
                className="mt-5 w-full h-13 py-3.5 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-bold text-[15.5px] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="demo-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {busy ? c.sending : c.cta}
              </button>
              <p className="mt-3 text-[11.5px] text-neutral-400 leading-relaxed text-center">
                {c.legal}{' '}
                <Link to="/terms" className="underline hover:text-neutral-600">{isEs ? 'Términos' : 'Terms'}</Link>
                {' · '}
                <Link to="/privacy" className="underline hover:text-neutral-600">{isEs ? 'Política de Privacidad' : 'Privacy Policy'}</Link>
              </p>
            </motion.form>
          )}

          <p className="mt-5 text-center text-[14px] text-neutral-500">
            {c.haveAccount}{' '}
            <Link to="/account" className="text-[#F77F11] font-bold hover:underline" data-testid="demo-login-link">{c.login} →</Link>
          </p>
        </div>

        {/* ---- Right: social proof ---- */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:sticky lg:top-28">
          <div className="rounded-3xl overflow-hidden relative">
            <img src="/auth/client.png" alt="" className="w-full h-[300px] sm:h-[360px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-6 right-6 flex items-center gap-1.5" aria-hidden>
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FFC439] text-[#FFC439]" />)}
              <span className="ml-2 text-white text-[13px] font-semibold drop-shadow">Kitxens Restaurant Operating Intelligence</span>
            </div>
          </div>
          <h2 className="mt-7 text-2xl font-bold text-neutral-900 leading-snug text-center">{c.proofT}</h2>
          <p className="mt-2 text-[14.5px] text-neutral-600 text-center">{c.proofSub}</p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {c.badges.map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-neutral-200 bg-white p-4 text-center">
                <p className="text-2xl font-bold text-[#F77F11]">{n}</p>
                <p className="mt-0.5 text-[12px] text-neutral-500 leading-snug">{l}</p>
              </div>
            ))}
          </div>
          <ul className="mt-6 space-y-2.5">
            {c.points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-[14.5px] text-neutral-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-neutral-400">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> WCAG 2.1 AA</span>
            <span className="inline-flex items-center gap-1.5"><Globe2 className="w-4 h-4" /> EN · ES</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {isEs ? 'Expertos reales + IA' : 'Real experts + AI'}</span>
            <span className="inline-flex items-center gap-1.5"><Utensils className="w-4 h-4" /> {isEs ? 'Solo restaurantes' : 'Restaurants only'}</span>
          </div>
        </motion.div>
      </div>

      <ScanPromo className="mt-16" />
    </div>
  );
}
